import { useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import InfoDividerDot from '@alltrails/core/components/InfoDividerDot';
import Typography from '@alltrails/core/components/Typography';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import Button from '@alltrails/denali/components/Button';
import Logo from '@alltrails/denali/components/Logo';
import TextArea from '@alltrails/denali/components/TextArea';
import type { ReviewReply } from '@alltrails/types/ReviewReply';
import styles from './styles/styles.module.scss';
import TrailReviewDate from './TrailReviewDate';

const messages = defineMessages({
  POST_PUBLIC_RESPONSE: { defaultMessage: 'Post public response' },
  CANCEL: { defaultMessage: 'Cancel' },
  DELETE: { defaultMessage: 'Delete' },
  RESPONSE: { defaultMessage: 'Response' }
});

export type TrailReviewReplyProps = {
  hideReplies?: boolean;
  isAdmin?: boolean;
  isEditing: boolean;
  isSaving?: boolean;
  onCancel: () => void;
  onDelete?: () => void;
  onSave?: (replyText: string) => void;
  reviewReply?: ReviewReply;
};

const TrailReviewReply = ({
  hideReplies,
  isAdmin,
  isEditing,
  isSaving,
  onCancel,
  onDelete,
  onSave,
  reviewReply
}: TrailReviewReplyProps) => {
  const {
    formattedDefaultMessages: { POST_PUBLIC_RESPONSE, CANCEL, DELETE, RESPONSE }
  } = useFormatMessage(messages);

  const [replyText, setReplyText] = useState(reviewReply?.comment ?? '');

  if (hideReplies || (!isEditing && !reviewReply)) {
    return <></>;
  }

  if (isEditing) {
    return (
      <div className={styles.replyEditContainer} data-at-theme-dimensions="compact">
        <TextArea
          className={styles.replyEditor}
          hideLabel
          labelText={RESPONSE}
          onChange={setReplyText}
          testId="trail-review-reply-text-area"
          value={replyText}
          rows={3}
          helperText={<FormattedMessage defaultMessage="All admin responses will appear as “Response from AllTrails”. Admin names are not shown." />}
        />
        <div className={styles.replyActions}>
          <Button onClick={onCancel} size="sm" text={CANCEL} variant="flat" />
          <Button
            loading={isSaving}
            onClick={() => {
              onSave?.(replyText);
            }}
            size="sm"
            text={POST_PUBLIC_RESPONSE}
            variant="primary"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.replyContainer}>
      <div className={styles.borderBlock} />
      <div className={styles.replyTextContainer}>
        <span>
          <div className={styles.newReviewTitle}>
            <div className={styles.logoBackground}>
              <Logo color="colored-light" size="sm" variant="symbol" />
            </div>
            <div className={styles.heading}>
              <Typography variant="text200bold">
                <FormattedMessage defaultMessage="Response from AllTrails" />
              </Typography>{' '}
              <div className={styles.subtext}>
                <Typography variant="text100" color="secondary">
                  <span suppressHydrationWarning>
                    <TrailReviewDate created={reviewReply!.metadata.created} />
                  </span>
                </Typography>
                {isAdmin && (
                  <>
                    <InfoDividerDot />
                    <Typography variant="text100" color="secondary">
                      <FormattedMessage defaultMessage="Written by" /> {reviewReply?.user?.firstName}
                    </Typography>
                  </>
                )}
              </div>
            </div>
          </div>
        </span>
        <span>{reviewReply!.comment}</span>
      </div>
      {isAdmin && (
        <Button
          onClick={() => {
            onDelete?.();
            setReplyText('');
          }}
          size="sm"
          text={DELETE}
          title={DELETE}
          variant="default"
        />
      )}
    </div>
  );
};

export default TrailReviewReply;
